import { MaerskColorPalette } from '@app/app.enum';
import { Box, CircularProgress } from '@material-ui/core';
import MaUTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import * as React from 'react';
import { Column, TableOptions, useExpanded, useSortBy, useTable } from 'react-table';

import Icon from './icon.component';
import NoDataComponent from './no-data.component';
import TablePaginationComponent from './table-pagination.component';

export interface ITableProps<T extends object> extends TableOptions<T> {
	contained?: boolean;
	pageSize?: number;
	currentPage?: number;
	totalRecords?: number;
	dataLoading?: boolean;
	canExpand?: boolean;
	onRowClick?: (row: T) => void | Promise<void>;
	onPageChange?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, page: number) => void | Promise<void>;
	rowContent?: (row: unknown) => JSX.Element;
	applyRowColor?: boolean;
	siblingCount?: number;
}

const expanderId = 'expander';

export const DefaultTableRowColor = 'white';
export const ShadedTableRowColor = MaerskColorPalette.LightGrey;

const Table = <T extends object>(props: ITableProps<T>) => {
	const { columns, data, pageSize, totalRecords, totalPages, currentPage, siblingCount } = props;

	/* pageJump is for keeping changes in text field of page jump,and if user applies the page jump
	by clicking, then only we will assign the value of page jump to currentPage */
	/*currentPage is used for TablePagination tag for displaying current page of data */

	if (props.canExpand && !columns.find((x) => x.id === expanderId)) {
		(columns as Column[]).unshift({
			// Make an expander cell
			Header: '', // No header
			id: expanderId, // It needs an ID
			Cell: ({ row }: any) => {
				return (
					// Use Cell to render an expander for each row.
					// We can use the getToggleRowExpandedProps prop-getter
					// to build the expander.
					<span {...row.getToggleRowExpandedProps()}>
						{row.isExpanded ? (
							<Icon name='chevron-up' fontSize='small' />
						) : (
							<Icon name='chevron-down' style={{ transform: 'translateY(-2.5px)' }} fontSize='small' />
						)}
					</span>
				);
			},
			disableSortBy: true,
			width: 60,
		});
	}

	const { getTableProps, headerGroups, rows, prepareRow, visibleColumns } = useTable<T>(props, useSortBy, useExpanded);

	return (
		<>
			<MaUTable {...getTableProps()}>
				<TableHead>
					{headerGroups.map((headerGroup) => (
						<TableRow {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<TableCell
									{...column.getHeaderProps(column.getSortByToggleProps())}
									sortDirection={column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : null}
									style={{
										width: column.width,
									}}
								>
									{column.render('Header')}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableHead>
				{props.dataLoading ? (
					<TableBody>
						<TableRow>
							<TableCell align='center' colSpan={columns.length}>
								<div>
									<CircularProgress size={32} />
								</div>
							</TableCell>
						</TableRow>
					</TableBody>
				) : data && data.length > 0 ? (
					<TableBody>
						{rows.map((row) => {
							prepareRow(row);
							return (
								<React.Fragment key={row.id}>
									<TableRow
										{...row.getRowProps()}
										style={{
											backgroundColor: props.applyRowColor
												? (row.original as any).backgroundColor
												: DefaultTableRowColor,
										}}
									>
										{row.cells.map((cell) => {
											return (
												<TableCell
													key={row.id}
													{...(cell as any).getCellProps({
														onClick: () => {
															if (cell.column.id !== expanderId && props.onRowClick) {
																props.onRowClick(row.original);
															}
														},
														className: props.onRowClick && 'pointer',
													})}
													style={{
														width: cell.column.width,
													}}
												>
													{cell.render('Cell')}
												</TableCell>
											);
										})}
									</TableRow>
									{/*
										If the row is in an expanded state, render a row with a
										column that fills the entire length of the table.
									*/}
									{row.isExpanded ? (
										<TableRow className='table-expanded-child'>
											<TableCell colSpan={visibleColumns.length}>
												{/*
													Inside it, call our renderRowSubComponent function. In reality,
													you could pass whatever you want as props to
													a component like this, including the entire
													table instance. But for this example, we'll just
													pass the row
												*/}
												{props.rowContent(row)}
											</TableCell>
										</TableRow>
									) : null}
								</React.Fragment>
							);
						})}
					</TableBody>
				) : (
					<NoDataComponent showTableNoData={true} />
				)}
			</MaUTable>
			<Box flex={true} flexGrow={1} mt={2}>
				{!!props.pageSize && (
					<TablePaginationComponent
						showPaginator={!props.contained}
						rowsPerPage={pageSize}
						totalPages={totalPages}
						count={totalRecords}
						page={currentPage}
						onChangePage={props.onPageChange}
						siblingCount={siblingCount}
					/>
				)}
			</Box>
		</>
	);
};
export default Table;



// WEBPACK FOOTER //
// ./src/components/shared/table.component.tsx